<template>
	<BaseModal
		:is-visible="isVisible"
		title="Sync Module Access"
		subtitle="Reset and re-assign 44-month module access for all employees"
		:icon="RefreshCw"
		size="lg"
		type="warning"
		:show-footer="true"
		@close="closeModal"
	>
		<div class="space-y-5">
			<div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
				<div class="flex items-start gap-3">
					<AlertTriangle class="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
					<div>
						<h4 class="text-sm font-semibold text-amber-800 dark:text-amber-200">Important</h4>
						<p class="text-sm text-amber-700 dark:text-amber-300 mt-1">
							This will remove current module-access assignments for active and inactive employees,
							then recreate access from January of the selected year for a full 44-month cycle.
						</p>
					</div>
				</div>
			</div>

			<!-- Start Year Selection -->
			<div>
				<BaseInput
					v-model="selectedYear"
					label="Start Year"
					type="select"
					:options="yearOptions"
					:required="true"
					helper-text="Module access will start from January of the selected year."
				/>
			</div>

			<div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
				<h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">What happens after sync</h4>
				<ul class="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc pl-5">
					<li>Existing module-access month assignments are cleared.</li>
					<li>Employees receive rebuilt assignments for the 44-month sequence starting January {{ selectedYear }}.</li>
					<li>Your employee list will refresh after success.</li>
				</ul>
			</div>

			<div v-if="errorMessage" class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
				<p class="text-sm text-red-700 dark:text-red-300">{{ errorMessage }}</p>
			</div>
		</div>

		<template #footer>
			<BaseButton
				text="Cancel"
				variant="secondary"
				:disabled="isSyncing"
				@click="closeModal"
			/>
			<BaseButton
				:text="isSyncing ? 'Syncing...' : 'Sync Access'"
				variant="warning"
				:icon="RefreshCw"
				:loading="isSyncing"
				:disabled="isSyncing"
				@click="handleSync"
			/>
		</template>
	</BaseModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import BaseInput from '@/components/common/BaseInput.vue'
import { useToastService } from '@/services/toast'
import { employeeService } from '@/services/user'
import { AlertTriangle, RefreshCw } from 'lucide-vue-next'

defineProps<{
	isVisible: boolean
}>()

const emit = defineEmits<{
	close: []
	success: [message: string]
}>()

const toast = useToastService()
const currentYear = new Date().getFullYear()
const accessStartYear = 2025
const selectedYear = ref<number>(accessStartYear)
const isSyncing = ref(false)
const errorMessage = ref('')

const yearOptions = computed(() => {
	const options = []
	for (let year = accessStartYear; year <= currentYear + 10; year++) {
		options.push({ value: year, label: String(year) })
	}
	return options
})

const closeModal = () => {
	if (isSyncing.value) return
	errorMessage.value = ''
	selectedYear.value = accessStartYear
	emit('close')
}

const handleSync = async () => {
	if (isSyncing.value) return

	isSyncing.value = true
	errorMessage.value = ''

	try {
		const result = await employeeService.syncModuleAccess(Number(selectedYear.value))
		toast.success('Sync Completed', result.message)
		emit('success', result.message)
		emit('close')
	} catch (error: any) {
		const message = error?.response?.data?.message || error?.message || 'Failed to sync module access.'
		errorMessage.value = message
		toast.error('Sync Failed', message)
	} finally {
		isSyncing.value = false
	}
}
</script>

<style scoped>
/* Uses shared modal and button styles from common components */
</style>
